import {
  formSelector,
  getState,
  onlyIfForm,
  removeForm,
  setForm,
  updateForm,
  useSelector,
} from '../../../store.js'
import ConfirmModal from '../../../common/components/Modal/ConfirmModal.js'
import {showMessageToast} from '../../Header/Toast/index.js'
import {
  labelConfigSelector,
  dimensionsSelector,
  dimensionHasError,
  orderTypeSelector,
  labelTypeSelector,
  orderNumberFromLabelConfigSelector,
  referenceIDFromLabelConfigSelector,
  updateParcel,
} from '../../../data/labelInfos/index.js'
import DimensionsInput from '../../../common/components/Form/DimensionsInput.js'
import {getBulkRates} from '../BulkLabelConfigForm/bulkLabelConfigActions.js'
import {getRates} from '../../../data/labelInfos/rateRequest.js'
import {LABEL_TYPE_SHIPPING} from '../../../common/constants/LabelConfig.js'
import {
  OT_RETURN_ORDER,
  OT_V3_ORDER,
} from '../../../common/constants/OrderTypes.js'
import {
  Count,
  IfPlural,
  IfSingle,
  Plural,
  PluralBlock,
} from '../../../common/components/Plural.js'

export const MODAL_FORM = 'edit_dimensions_modal'

export async function showEditDimensionsModal(labelInfoIDs, packagesIndex) {
  const dimensions = dimensionsSelector(getState(), {
    labelInfoID: labelInfoIDs[0],
    packagesIndex: packagesIndex || 0,
  })
  const orderType = orderTypeSelector(getState(), {
    labelInfoID: labelInfoIDs[0],
  })

  // count up shipping labels to know if this is mixed with shipping and return labels
  const shippingCount = labelInfoIDs.reduce(
    (count, labelInfoID) =>
      count +
      (labelTypeSelector(getState(), {labelInfoID}) === LABEL_TYPE_SHIPPING
        ? 1
        : 0),
    0,
  )

  // item count is
  // shipping label count if we more labelInfoIDs than shipping labels
  // otherwise labelInfoID count
  const itemCount =
    shippingCount && shippingCount !== labelInfoIDs.length
      ? shippingCount
      : labelInfoIDs.length

  const orderNumber =
    orderType === OT_V3_ORDER && itemCount === 1
      ? orderNumberFromLabelConfigSelector(getState(), {
          labelInfoID: labelInfoIDs[0],
        })
      : null
  const referenceID =
    orderType === OT_RETURN_ORDER && itemCount === 1
      ? referenceIDFromLabelConfigSelector(getState(), {
          labelInfoID: labelInfoIDs[0],
        })
      : null

  setForm(MODAL_FORM, {
    labelInfoIDs,
    orderType,
    itemCount,
    orderNumber,
    referenceID,
    packagesIndex,
    length: dimensions.length,
    width: dimensions.width,
    height: dimensions.height,
    isSaving: false,
    serverError: null,
  })
}

export function updateModalForm(updates) {
  updateForm(MODAL_FORM, updates)
}

export function closeModal() {
  removeForm(MODAL_FORM)
}

export function modalFormSelector(state) {
  return formSelector(state, {formName: MODAL_FORM})
}

export function errorsSelector(state) {
  const {length, width, height} = modalFormSelector(state)

  const errors = {}

  if (dimensionHasError(length)) {
    errors.length = 'Invalid dimension'
    errors.preventSave = true
  }

  if (dimensionHasError(width)) {
    errors.width = 'Invalid dimension'
    errors.preventSave = true
  }

  if (dimensionHasError(height)) {
    errors.height = 'Invalid dimension'
    errors.preventSave = true
  }

  return errors
}

export async function saveDimensions() {
  try {
    updateModalForm({isSaving: true, serverError: null})

    let {labelInfoIDs, packagesIndex, length, width, height} =
      modalFormSelector(getState())

    length = Number(length)
    width = Number(width)
    height = Number(height)

    for (const labelInfoID of labelInfoIDs) {
      const labelConfig = labelConfigSelector(getState(), {labelInfoID})

      for (const index of packagesIndex === undefined
        ? labelConfig.packages.keys()
        : [packagesIndex]) {
        updateParcel(labelInfoID, index, {length, width, height})
      }
    }

    showMessageToast('Dimensions have been updated')

    closeModal()

    await getBulkRates(labelInfoIDs)

    await getRates(labelInfoIDs)
  } catch (error) {
    updateModalForm({
      serverError: error.message || error.error_message,
      isSaving: false,
    })
  }
}

function EditDimensionsModal({form}) {
  const errors = useSelector(errorsSelector)

  return (
    <ConfirmModal
      title="Edit Dimensions"
      confirmText="Save"
      cancelText="Cancel"
      modalSize="x-sm"
      isDisabled={errors.preventSave}
      isSaving={form.isSaving}
      onConfirm={() => saveDimensions()}
      onCancel={() => closeModal()}
      error={form.serverError}
    >
      <ul className="list">
        <PluralBlock count={form.itemCount}>
          <li className="list__item--form list__item--no-style fs-n0 text--md-grey">
            <strong>
              Editing{' '}
              <IfSingle>{form.orderNumber || form.referenceID}</IfSingle>
              <IfPlural>
                <Count />{' '}
                <Plural
                  word={form.orderType === OT_V3_ORDER ? 'Order' : 'RMA'}
                />
              </IfPlural>
            </strong>
          </li>
        </PluralBlock>
        <li className="list__item--form list__item--no-style flex">
          <DimensionsInput
            length={form.length}
            width={form.width}
            height={form.height}
            setLength={(length) => updateModalForm({length})}
            setWidth={(width) => updateModalForm({width})}
            setHeight={(height) => updateModalForm({height})}
            errorMessage={errors.height || errors.length || errors.width}
            hasLengthError={!!errors.length}
            hasWidthError={!!errors.width}
            hasHeightError={!!errors.height}
            // onEnterKeyPress={() => !errors.preventSave && saveDimensions()}
            autoFocus
          />
        </li>
      </ul>
    </ConfirmModal>
  )
}

export default onlyIfForm(EditDimensionsModal, modalFormSelector)
